// The one IndexedDB store factory, shared by both of this app's databases.
//
// #207's U0 and #208. Two things live here: the plumbing two databases would
// otherwise each spell out, and the **probe by use** that decides whether this
// browser can give us a durable store at all.
//
// ── Why by use, and not by presence (#208) ─────────────────────────────────
//
// A presence test — `globalThis.indexedDB ? idb : memory` — reads the wrong
// thing. A browser that exposes the object and refuses to open a database — a
// private window in some engines, a browser with site data blocked — passes
// that test and would get a store whose every call rejects, with the write
// reported owed and nothing durable behind it.
//
// So the probe is the thing itself: open the database, then put and delete one
// probe row **inside a single readwrite transaction**, the narrowest operation
// that proves the whole path. `outstandingWrites.js` does the same for
// `localStorage`.
//
// The probe row is written and deleted in one transaction, so it is atomic: a
// tab that dies mid-probe leaves nothing behind, and there is no key to clean
// up on a later run.
//
// ── What the probe does *not* cover ────────────────────────────────────────
//
// It proves the store was usable **at first use**, not that it stays usable:
// a quota exceeded an hour later, a database deleted from under a live handle,
// a version change from another tab. Those still reject, and are **not**
// turned into a fallback to memory: by then rows have been written that the
// memory arm does not have, and a store missing data that looks healthy is
// worse than a rejection the caller can surface.
//
// The warning quotes the error it caught rather than naming a cause: a refused
// open and a corrupt database arrive here identically.
//
// ── Three kinds ─────────────────────────────────────────────────────────────
//
// `describe()` reports `memory`, `indexeddb`, or `memory-degraded`, and carries
// a `reason` for the last. `memory` is a store that was *asked* for memory —
// the `ui` project's seam. `memory-degraded` is a store that wanted IndexedDB
// and could not have it, which stays reportable (#208).
//
// ── Absence is checked first, and is silent ─────────────────────────────────
//
// `auto` tests for the object before probing and takes its absence as the
// answer. That case is **not** warned about, because it is the shape jsdom has
// and the `ui` project drives the queue there; a warning on every such test
// would bury a real one. A **real** browser that exposes no `indexedDB`
// therefore degrades quietly, though `describe()` still answers
// `memory-degraded` with a reason.
//
// ── The memory arm behaves like a database ─────────────────────────────────
//
//   * **Key order.** `getAll()` returns rows in key order; a `Map` returns them
//     in insertion order. The memory arm sorts, so both arms agree.
//   * **A row handed out is a copy**, so a caller mutating what it read cannot
//     edit the store. IndexedDB copies a row through the **structured clone**
//     algorithm, so `clone()` calls `structuredClone` when the realm has one.
//     JSON is not equivalent: through it the two arms would disagree on a
//     `NaN`, an `Infinity`, a property set to `undefined`, and a `Date` (#266).
//
//     The JSON fallback is for a realm with no `structuredClone` — the `ui`
//     jest project is one (`idbStore.test.jsx`). Its divergence: `NaN` and
//     `Infinity` become `null`, an `undefined`-valued key is dropped, and a
//     `Date` becomes an ISO string. A class instance (a Firestore `Timestamp`)
//     loses its prototype either way — `structuredClone` hands back
//     `{seconds, nanoseconds}` — so that is not a divergence between the arms.
//
//     Callers hand over JSON (#265): `appStore.js` runs every document through
//     `docShape.js` **before** it reaches this file, so a `Timestamp` becomes
//     `{"$timestamp":"…"}` while it still has its `toDate()`. That is a duty of
//     the callers, and this file cannot check it.
//
// A `patch` of a missing row returns `null` in both arms (#251).

const PROBE_STORE_KEY = '__coda.probe__';

/**
 * A database.
 *
 * @param name     IndexedDB database name. Ours, sharing nothing with
 *                 Firestore's — see `queueStore.js`'s header for why.
 * @param version  Schema version. Bump when `stores` changes.
 * @param stores   `[{ name, keyPath }]`. One transaction may span any of them.
 * @param engine   `'auto'` (probe, degrade), `'indexeddb'` (probe, degrade —
 *                 same thing, kept for the named export next door) or
 *                 `'memory'` (the test seam; never touches IndexedDB).
 * @param lost     One clause, in the teacher's terms, naming what a reload
 *                 forgets when this degrades. Quoted in the warning.
 */
export function createLocalDatabase({
  name,
  version = 1,
  stores,
  engine = 'auto',
  lost = 'data is held in memory only, so a reload will forget it',
}) {
  if (engine === 'memory') return memoryEngine({ name, stores, kind: 'memory' });
  if (!hasIndexedDb()) {
    return memoryEngine({
      name,
      stores,
      kind: 'memory-degraded',
      reason: 'this realm exposes no indexedDB',
    });
  }
  return probingEngine({ name, version, stores, lost });
}

// Reading the property can itself throw in a sandboxed frame, which is why this
// is a try rather than a truthiness test.
function hasIndexedDb() {
  try {
    return Boolean(globalThis.indexedDB);
  } catch {
    return false;
  }
}

// ── memory ──────────────────────────────────────────────────────────────────

// Reading the property can itself throw in a sandboxed frame, the same reason
// `hasIndexedDb()` above is a try rather than a truthiness test.
function hasStructuredClone() {
  try {
    return typeof globalThis.structuredClone === 'function';
  } catch {
    return false;
  }
}

function clone(row) {
  if (hasStructuredClone()) return globalThis.structuredClone(row);
  // The fallback, and its divergence from the real algorithm: see the header.
  return JSON.parse(JSON.stringify(row));
}

function byKey(a, b) {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

function memoryEngine({ name, stores, kind, reason = null }) {
  const tables = new Map(
    stores.map((s) => [s.name, { keyPath: s.keyPath, rows: new Map() }]),
  );
  const table = (storeName) => {
    const t = tables.get(storeName);
    if (!t) throw new Error(`[store] unknown object store "${storeName}" in "${name}"`);
    return t;
  };
  const keyOf = (storeName, row) => row[table(storeName).keyPath];

  const applyOne = (op) => {
    const t = table(op.store);
    if (Object.prototype.hasOwnProperty.call(op, 'put')) {
      t.rows.set(keyOf(op.store, op.put), clone(op.put));
    } else if (Object.prototype.hasOwnProperty.call(op, 'remove')) {
      t.rows.delete(op.remove);
    } else if (Object.prototype.hasOwnProperty.call(op, 'removeRange')) {
      const [lower, upperExclusive] = op.removeRange;
      [...t.rows.keys()]
        .filter((k) => k >= lower && k < upperExclusive)
        .forEach((k) => t.rows.delete(k));
    } else if (op.clear) {
      t.rows.clear();
    } else {
      throw new Error(`[store] unrecognised op on "${op.store}"`);
    }
  };

  return {
    async describe() {
      return { kind, database: name, reason };
    },
    async get(storeName, key) {
      const row = table(storeName).rows.get(key);
      return row === undefined ? null : clone(row);
    },
    async all(storeName) {
      return [...table(storeName).rows.entries()]
        .sort((a, b) => byKey(a[0], b[0]))
        .map(([, row]) => clone(row));
    },
    async range(storeName, lower, upperExclusive) {
      return [...table(storeName).rows.entries()]
        .filter(([k]) => k >= lower && k < upperExclusive)
        .sort((a, b) => byKey(a[0], b[0]))
        .map(([, row]) => clone(row));
    },
    async patch(storeName, key, changes) {
      const t = table(storeName);
      const row = t.rows.get(key);
      if (!row) return null;
      const next = { ...row, ...changes };
      t.rows.set(key, next);
      return clone(next);
    },
    async apply(ops) {
      // Applied against a scratch copy first so a bad op in the middle of a
      // list leaves nothing half-done, which is what the transaction gives the
      // other arm. Cheap: these lists are a collection window at most.
      const undo = ops.map((op) => op.store).map((s) => [s, new Map(table(s).rows)]);
      try {
        ops.forEach(applyOne);
      } catch (err) {
        undo.forEach(([s, rows]) => { table(s).rows = rows; });
        throw err;
      }
    },
  };
}

// ── IndexedDB ───────────────────────────────────────────────────────────────

function open({ name, version, stores }) {
  return new Promise((resolve, reject) => {
    // `globalThis.indexedDB` may be absent, or may throw on access in a
    // sandboxed frame. Both land in the caller's rejection handler because the
    // whole body is inside the promise executor.
    const request = globalThis.indexedDB.open(name, version);
    request.onupgradeneeded = () => {
      const db = request.result;
      stores.forEach(({ name: storeName, keyPath }) => {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath });
        }
      });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('indexedDB.open failed'));
    // A version change another tab is holding open. Not an error yet, but it
    // never resolves either, so it is the one case that would hang forever.
    request.onblocked = () => reject(new Error('indexedDB.open blocked by another tab'));
  });
}

function transact(db, storeNames, mode, fn) {
  return new Promise((resolve, reject) => {
    let tx;
    try {
      tx = db.transaction(storeNames, mode);
    } catch (err) {
      reject(err);
      return;
    }
    let result;
    // **Resolve on `tx.oncomplete`, not on the request's `onsuccess`.** The
    // request succeeding means the operation was accepted; the transaction
    // completing means it is on disk. The write queue's contract — a caller's
    // promise does not resolve until the write is durably owed — rests on that
    // difference, which shows when a tab closes.
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error || new Error('transaction failed'));
    tx.onabort = () => reject(tx.error || new Error('transaction aborted'));
    try {
      fn(
        (storeName) => tx.objectStore(storeName),
        (value) => { result = value; },
      );
    } catch (err) {
      try { tx.abort(); } catch { /* already gone */ }
      reject(err);
    }
  });
}

// The probe: open, then prove a readwrite transaction against a real object
// store. Both halves are needed — an engine can hand back a database handle and
// refuse to write to it.
async function probe({ name, version, stores }) {
  const db = await open({ name, version, stores });
  const first = stores[0];
  await transact(db, [first.name], 'readwrite', (store) => {
    const s = store(first.name);
    s.put({ [first.keyPath]: PROBE_STORE_KEY, probe: true });
    s.delete(PROBE_STORE_KEY);
  });
  return db;
}

function probingEngine({ name, version, stores, lost }) {
  let resolution = null;

  // Two-argument `then`, not `.then().catch()`. The rejection handler here is
  // for *the probe's* failure; a trailing catch would also swallow any
  // programming error thrown while building the fallback (#184).
  const resolve = () => {
    if (!resolution) {
      resolution = probe({ name, version, stores }).then(
        (db) => ({ db }),
        (error) => {
          // One line, once, and it quotes what it saw. It does not name a
          // cause: a refused open, a blocked version change and a corrupt
          // database arrive here identically, and this cannot tell them apart.
          console.warn(
            `[store] "${name}" could not be opened for use, so ${lost} (#208). `
            + `The store degraded to memory after: ${error && error.message ? error.message : error}`,
          );
          return { fallback: memoryEngine({
            name,
            stores,
            kind: 'memory-degraded',
            reason: error && error.message ? error.message : String(error),
          }) };
        },
      );
    }
    return resolution;
  };

  const storeNamesOf = (ops) => [...new Set(ops.map((op) => op.store))];

  return {
    async describe() {
      const r = await resolve();
      if (r.fallback) return r.fallback.describe();
      return { kind: 'indexeddb', database: name, reason: null };
    },

    async get(storeName, key) {
      const r = await resolve();
      if (r.fallback) return r.fallback.get(storeName, key);
      const row = await transact(r.db, [storeName], 'readonly', (store, set) => {
        const req = store(storeName).get(key);
        req.onsuccess = () => set(req.result);
      });
      return row === undefined ? null : row;
    },

    async all(storeName) {
      const r = await resolve();
      if (r.fallback) return r.fallback.all(storeName);
      const rows = await transact(r.db, [storeName], 'readonly', (store, set) => {
        const req = store(storeName).getAll();
        req.onsuccess = () => set(req.result || []);
      });
      return rows || [];
    },

    async range(storeName, lower, upperExclusive) {
      const r = await resolve();
      if (r.fallback) return r.fallback.range(storeName, lower, upperExclusive);
      const rows = await transact(r.db, [storeName], 'readonly', (store, set) => {
        const req = store(storeName).getAll(
          globalThis.IDBKeyRange.bound(lower, upperExclusive, false, true),
        );
        req.onsuccess = () => set(req.result || []);
      });
      return rows || [];
    },

    async patch(storeName, key, changes) {
      const r = await resolve();
      if (r.fallback) return r.fallback.patch(storeName, key, changes);
      return transact(r.db, [storeName], 'readwrite', (store, set) => {
        const s = store(storeName);
        const req = s.get(key);
        req.onsuccess = () => {
          const row = req.result;
          // `null`, not `undefined`, and the same as the memory arm. #251.
          if (!row) { set(null); return; }
          const next = { ...row, ...changes };
          s.put(next);
          set(next);
        };
      });
    },

    async apply(ops) {
      if (!ops.length) return;
      const r = await resolve();
      if (r.fallback) return r.fallback.apply(ops);
      // One transaction over every store the list touches, so a window of
      // documents and the receipt that says the window is complete land
      // together or not at all.
      return transact(r.db, storeNamesOf(ops), 'readwrite', (store) => {
        ops.forEach((op) => {
          const s = store(op.store);
          if (Object.prototype.hasOwnProperty.call(op, 'put')) s.put(op.put);
          else if (Object.prototype.hasOwnProperty.call(op, 'remove')) s.delete(op.remove);
          else if (Object.prototype.hasOwnProperty.call(op, 'removeRange')) {
            const [lower, upperExclusive] = op.removeRange;
            s.delete(globalThis.IDBKeyRange.bound(lower, upperExclusive, false, true));
          } else if (op.clear) s.clear();
          else throw new Error(`[store] unrecognised op on "${op.store}"`);
        });
      });
    },
  };
}
